import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip, Avatar, Badge, Tooltip } from '@mui/material';
import { 
  LocationOn, 
  DriveEta, 
  DirectionsCar, 
  PhoneIphone, 
  PersonPin,
  Navigation,
  Circle
} from '@mui/icons-material';
import { liveOperationsService, LiveLocation } from '@/services/liveOperationsService';

// Enhanced map component with mobile user tracking
export const InteractiveMap: React.FC = () => {
  const [locations, setLocations] = useState<LiveLocation[]>([]);
  const [realTimeStats, setRealTimeStats] = useState({
    activeTrips: 0,
    driversOnline: 0,
    mobileUsersActive: 0,
    currentBookingSessions: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch real locations data
    const fetchLocations = async () => {
      try {
        const liveData = await liveOperationsService.getLiveOperations();
        
        setLocations(liveData.locations);
        
        // Calculate real-time stats from actual data
        const stats = {
          activeTrips: liveData.locations.filter(l => l.type === 'active-trip').length,
          driversOnline: liveData.locations.filter(l => l.type === 'driver').length,
          mobileUsersActive: liveData.locations.filter(l => l.type === 'mobile-user').length,
          currentBookingSessions: liveData.locations.filter(l => l.type === 'booking-session').length,
        };

        setRealTimeStats(stats);

      } catch (error) {
        console.error('❌ Error fetching locations:', error);
        // Keep existing mock data if API fails
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();

    // Set up real-time updates every 15 seconds
    const interval = setInterval(fetchLocations, 15000);
    
    return () => clearInterval(interval);
  }, []);

  const getLocationIcon = (location: LiveLocation) => {
    switch (location.type) {
      case 'active-trip':
        return <DirectionsCar sx={{ fontSize: 16 }} />;
      case 'driver':
        return <DriveEta sx={{ fontSize: 16 }} />;
      case 'mobile-user':
        return <PhoneIphone sx={{ fontSize: 14 }} />;
      case 'booking-session':
        return <PersonPin sx={{ fontSize: 16 }} />;
      default:
        return <Circle sx={{ fontSize: 12 }} />;
    }
  };

  const getLocationColor = (location: LiveLocation) => {
    switch (location.type) {
      case 'active-trip':
        return 'primary.main';
      case 'driver':
        return location.status === 'available' ? 'success.main' : 'warning.main';
      case 'mobile-user':
        return 'info.main';
      case 'booking-session':
        return 'secondary.main';
      default:
        return 'grey.500';
    }
  };

  const getActivityIndicator = (location: LiveLocation) => {
    if (location.type === 'mobile-user' || location.type === 'booking-session') {
      return (
        <Badge
          badgeContent={location.platform === 'ios' ? 'iOS' : 'Android'}
          color={location.platform === 'ios' ? 'primary' : 'success'}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.6rem',
              height: 16,
              minWidth: 16,
            }
          }}
        >
          {getLocationIcon(location)}
        </Badge>
      );
    }
    return getLocationIcon(location);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        minHeight: 320,
        backgroundColor: '#f5f5f5',
        backgroundImage: `
          linear-gradient(45deg, #e0e0e0 25%, transparent 25%),
          linear-gradient(-45deg, #e0e0e0 25%, transparent 25%),
          linear-gradient(45deg, transparent 75%, #e0e0e0 75%),
          linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)
        `,
        backgroundSize: '20px 20px',
        backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
        borderRadius: '0 0 8px 8px',
        overflow: 'hidden',
      }}
    >
      {/* Map Overlay with mock locations */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(76, 175, 80, 0.1) 100%)',
        }}
      >
        {/* Real-time location markers */}
        {locations.map((location, index) => (
          <Box
            key={location.id}
            sx={{
              position: 'absolute',
              top: `${20 + index * 15}%`,
              left: `${15 + index * 18}%`,
              transform: 'translate(-50%, -50%)',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translate(-50%, -50%) scale(1.1)',
              },
            }}
          >
            <Tooltip 
              title={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {location.label}
                  </Typography>
                  <Typography variant="caption">
                    Status: {location.status}
                  </Typography>
                  {location.activity && (
                    <Typography variant="caption" display="block">
                      Activity: {location.activity}
                    </Typography>
                  )}
                  {location.duration && (
                    <Typography variant="caption" display="block">
                      Duration: {location.duration}
                    </Typography>
                  )}
                </Box>
              }
              arrow
              placement="top"
            >
              <Avatar
                sx={{
                  width: location.type === 'mobile-user' ? 28 : 32,
                  height: location.type === 'mobile-user' ? 28 : 32,
                  backgroundColor: getLocationColor(location),
                  border: '2px solid white',
                  boxShadow: 2,
                  animation: location.type === 'booking-session' ? 'pulse 2s infinite' : 'none',
                  '@keyframes pulse': {
                    '0%': { boxShadow: '0 0 0 0 rgba(63, 81, 181, 0.7)' },
                    '70%': { boxShadow: '0 0 0 10px rgba(63, 81, 181, 0)' },
                    '100%': { boxShadow: '0 0 0 0 rgba(63, 81, 181, 0)' },
                  },
                }}
              >
                {getActivityIndicator(location)}
              </Avatar>
            </Tooltip>
          </Box>
        ))}

      </Box>

      {/* Map Controls */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Chip
          icon={<LocationOn />}
          label="Lagos, Nigeria"
          size="small"
          sx={{ backgroundColor: 'background.paper' }}
        />
        <Chip
          label={`${realTimeStats.mobileUsersActive} Active Users`}
          size="small"
          color="info"
          sx={{ backgroundColor: 'background.paper' }}
        />
      </Box>

      {/* Legend */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Chip
          icon={<DirectionsCar />}
          label={`${realTimeStats.activeTrips} Trips`}
          size="small"
          color="primary"
          variant="filled"
        />
        <Chip
          icon={<DriveEta />}
          label={`${realTimeStats.driversOnline} Drivers`}
          size="small"
          color="success"
          variant="filled"
        />
        <Chip
          icon={<PhoneIphone />}
          label="Mobile Users"
          size="small"
          color="info"
          variant="filled"
        />
        <Chip
          icon={<PersonPin />}
          label={`${realTimeStats.currentBookingSessions} Booking`}
          size="small"
          color="secondary"
          variant="filled"
        />
      </Box>

      {/* Placeholder text for real map integration */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          opacity: 0.3,
        }}
      >
        <LocationOn sx={{ fontSize: 48, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">
          Live Mobile Activity Map
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Real-time user locations and activities
        </Typography>
      </Box>
    </Box>
  );
};